import * as React from 'react'
import * as Redux from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import styled, { css } from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as dateUtils from '@owl-nest/date'
import { t, tn, tnc } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import * as plume from '@ulule/owl-kit-components/next'
import { Reward } from '@owl-nest/models/src/reward'
import * as utils from '@owl-nest/utils'
import * as env from '@owl-nest/config'

import { RootState } from '../../../../../reducers'

import { useDisplayMode } from '../../../../hooks/useDisplayMode'
import { useNavigation } from '../../../../hooks/useNavigation'
import { PATHS } from '../../../../paths'
import { Project } from '../../../../models/project'
import * as projectModel from '../../../../models/project'

import { EditButton } from '../../components/EditButton'
import * as styles from '../../style'

import { User } from './component/User'

type NewsProps = {
  news: api.News<boolean, true>
  full?: boolean
}

export function News({ news, full = false }: NewsProps): React.ReactElement<NewsProps> {
  const navigation = useNavigation()

  // Toggle expand NewsBody
  const [isExpanded, setIsExpanded] = React.useState(false)

  const targetRef = React.useRef<HTMLDivElement>(null)

  const [folded, setFolded] = React.useState<{ withText: boolean; withPicture: boolean }>({
    withText: false,
    withPicture: false,
  })

  React.useLayoutEffect(() => {
    if (targetRef.current && !full) {
      const height = targetRef.current.offsetHeight
      const children = targetRef.current.children
      if (children.length > 0) {
        let withText = height >= 150
        let withPicture = false

        // First child is always a <p> because of CKEditor
        const firstChild = children[0]
        if (firstChild.children.length > 0) {
          withText = firstChild.children[0].tagName !== 'IMG' && height >= 150
          withPicture = firstChild.children[0].tagName === 'IMG'
        }
        setFolded({ withText, withPicture })
      }
    }
  }, [])

  const formattedDate = dateUtils.format(new Date(news.date_publication ?? news.date_creation), 'dd MMMM yyyy')
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)

  const auth = useAuth()
  const isUserStaff = auth.type === 'loggedin' && auth.user.is_staff
  const isUserAllowed = Boolean(news.permissions.self?.includes('read'))
  const isNewsMasked = !isUserAllowed
  const canUserEdit = useDisplayMode(project) === 'edit' && !model.project.refused<projectModel.ExtraFields>(project)
  const tag = news.author && model.user.tag(news.author, news.author.role)

  const projectTeamRoles = [api.UserRole.Owner, api.UserRole.Editor, api.UserRole.Moderator]
  const isProjectTeam = project.user_role && projectTeamRoles.includes(project.user_role)

  const hideComments =
    project.comments_enabled === api.ProjectCommentsPermission.NOONE && !isProjectTeam && !isUserStaff
  const disallowFirstComment =
    project.comments_enabled === api.ProjectCommentsPermission.NOONE ||
    (project.comments_enabled === api.ProjectCommentsPermission.SUPPORTERS && !isUserAllowed)
  const showCommentsLabel =
    !hideComments && (news.comments_count > 0 || (news.comments_count === 0 && !disallowFirstComment))

  const isScheduled = news.status === api.NewsStatus.SCHEDULED
  const isWaiting = news.status === api.NewsStatus.WAITING

  const userConfig = env.useUserEnv()

  return (
    <NewsBlock>
      {(news.audience_type == api.NewsAudienceType.SUPPORTERS || news.audience_type == api.NewsAudienceType.TIP) && (
        <plume.Callout type="info">
          {news.rewards
            ? tnc(
                'Publication only visible to backers of the reward [rewards]',
                'Publication only visible to backers of the rewards [rewards]',
                news.rewards.length,
                {
                  rewards: () => {
                    if (news.rewards) {
                      const rewardsToString = news.rewards.map((reward, index) => {
                        const title = reward.title
                          ? model.reward.title(reward as Reward)
                          : utils.text.truncate(utils.text.stripTags(reward.description[userConfig.lang]), 50)
                        return (
                          news.rewards && (
                            <span key={index}>
                              <i>{`"${title}"`}</i>
                              {index === news.rewards.length - 2 ? (
                                <span>{` ${t('and')} `}</span>
                              ) : index < news.rewards.length - 1 ? (
                                <span>{`, `}</span>
                              ) : (
                                ''
                              )}
                            </span>
                          )
                        )
                      })

                      if (news.audience_type == api.NewsAudienceType.TIP) {
                        rewardsToString.unshift(
                          <span key={-1}>
                            <i>{`"${t('Free donation')}"`}</i>
                            {news.rewards.length > 1 ? <span>{`, `}</span> : <span>{` ${t('and')} `}</span>}
                          </span>,
                        )
                      }

                      return rewardsToString
                    }
                  },
                },
              )
            : news.audience_type == api.NewsAudienceType.TIP
              ? t('This post is only visible to free donators')
              : t('This post is only visible to contributors')}
        </plume.Callout>
      )}
      {news.audience_type == api.NewsAudienceType.PAYING && (
        <plume.Callout type="info">{t('This post is only visible to contributors')}</plume.Callout>
      )}
      {news.audience_type == api.NewsAudienceType.FANS && (
        <plume.Callout type="info">{t('This post is only visible to fans')}</plume.Callout>
      )}
      <NewsHeader>
        <PublicationDate masked={isNewsMasked}>
          {isWaiting ? (
            <>
              <plume.glyphs.stroke.Unread size={12} />
              {t('Draft (only visible by you)')}
            </>
          ) : isScheduled && news.date_schedule ? (
            <>
              <ScheduledTag color="yellow">{t('Scheduled')}</ScheduledTag>
              {t('Post scheduled for %(date)s at %(time)s', {
                date: dateUtils.format(
                  new Date(news.date_schedule),
                  env.USER_COUNTRY === 'FR' ? 'dd/MM/yyyy' : 'MM/dd/yyyy',
                ),
                time: dateUtils.format(new Date(news.date_schedule), 'HH:mm'),
              })}
            </>
          ) : isNewsMasked ? (
            formattedDate
          ) : (
            <plume.Link
              forwardedAs={RouterLink}
              kind="secondary"
              to={navigation.pathTo({ path: PATHS.NEWS_PERMALINK, params: { slug: `${news.slug}-${news.id}` } })}
            >
              {formattedDate}
            </plume.Link>
          )}
        </PublicationDate>
        {canUserEdit && (
          <EditButtonWrapper>
            <EditButton link={`/projects/${project.id}/backoffice/news/${news.id}`} />
          </EditButtonWrapper>
        )}
      </NewsHeader>
      <NewsBody
        foldedWithText={folded.withText && !isExpanded && !isNewsMasked}
        foldedWithPicture={folded.withPicture && !isExpanded && !isNewsMasked}
      >
        {isNewsMasked ? (
          <BlurredBackground>
            <plume.glyphs.stroke.Unread />
            {(news.audience_type == api.NewsAudienceType.TIP ||
              news.audience_type == api.NewsAudienceType.SUPPORTERS) && (
              <>
                <plume.styles.copy.M>{t('This post is for backers only')}</plume.styles.copy.M>
                {!model.project.isFinished<projectModel.ExtraFields>(project) && (
                  <plume.styles.button.Button as={RouterLink} to={navigation.pathTo(PATHS.REWARDS)}>
                    {t('Back project')}
                  </plume.styles.button.Button>
                )}
              </>
            )}
            {news.audience_type == api.NewsAudienceType.FANS && (
              <>
                <plume.styles.copy.M>{t('This post is for fans only')}</plume.styles.copy.M>
              </>
            )}
          </BlurredBackground>
        ) : (
          <>
            {isWaiting ? (
              <Title draft={isWaiting}>{model.i18n.get(news.title, 'en', '')}</Title>
            ) : (
              <plume.Link
                forwardedAs={RouterLink}
                to={navigation.pathTo({ path: PATHS.NEWS_PERMALINK, params: { slug: `${news.slug}-${news.id}` } })}
              >
                <Title draft={isWaiting}>{model.i18n.get(news.title, 'en', '')}</Title>
              </plume.Link>
            )}
            <NewsContent
              ref={targetRef}
              dangerouslySetInnerHTML={{ __html: model.i18n.get(news.content ?? news.preview, 'en', '') }}
              textSize="small"
            />
            {(folded.withText || folded.withPicture) && !isExpanded && (
              <Expand kind="primary" onClick={() => setIsExpanded(true)} uppercase>
                {t('Read more')}
              </Expand>
            )}
          </>
        )}
      </NewsBody>
      {!isWaiting && (
        <NewsFooter masked={isNewsMasked}>
          {news.author && (
            <>
              <User user={news.author} tag={tag} />
              {showCommentsLabel && (
                <LinkWithIcon
                  as={RouterLink}
                  to={{
                    ...navigation.pathTo({
                      path: PATHS.NEWS_PERMALINK,
                      params: { slug: `${news.slug}-${news.id}` },
                    }),
                    hash: 'root-comment-form',
                  }}
                >
                  <plume.glyphs.stroke.Bubble size={16} />
                  <span>
                    {news.comments_count > 0
                      ? tn('%(count)s comment', '%(count)s comments', news.comments_count, {
                          count: news.comments_count,
                        })
                      : t('Write a comment')}
                  </span>
                </LinkWithIcon>
              )}
            </>
          )}
        </NewsFooter>
      )}
    </NewsBlock>
  )
}

// Block
const NewsBlock = styled.div`
  padding: 0 20px;
  background-color: ${plume.COLORS.PRIMARY_WHITE};
  box-shadow: ${plume.EFFECTS.SHADOW_2};
  border: 1px solid ${plume.COLORS.GREY_SHADE_5};
  margin-bottom: 40px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0px;
    left: 50%;
    bottom: -40px;
    border: 1px dashed ${plume.COLORS.GREY_SHADE_4};
    background: ${plume.COLORS.PRIMARY_WHITE};
  }

  ${plume.Callout} {
    margin-top: 20px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 0 30px;
  }
`

// Header
const NewsHeader = styled.div`
  padding: 20px 0;
  position: relative;
  z-index: 1;
`

const EditButtonWrapper = styled.div`
  position: absolute;
  right: 0px;
  top: 20px;
`

const PublicationDate = styled(plume.styles.heading.XXXXXS)<{ masked: boolean }>`
  filter: ${({ masked }) => masked && 'blur(8px)'};
  pointer-events: ${({ masked }) => masked && 'none'};

  ${plume.glyphs.stroke.Unread} {
    vertical-align: baseline;
    margin-right: 5px;
  }
`

const Title = styled(plume.styles.heading.L)<{ draft: boolean }>`
  display: inline-block;

  ${({ draft }) =>
    draft &&
    css`
      background-color: ${plume.COLORS.hexToRgb(plume.COLORS.BRAND_ACCENT_YELLOW, 0.3)};
    `}
`

const ScheduledTag = styled(plume.Tag)`
  margin-right: 4px;
`

// Body
const NewsContent = styled(plume.WysiwygContent).attrs((props) => ({ ...props, as: plume.styles.copy.S }))``

const NewsBody = styled.div<{ foldedWithText: boolean; foldedWithPicture: boolean }>`
  padding: 0 0 18px;
  margin-top: -15px;
  max-height: ${({ foldedWithText }) => (foldedWithText ? '150px' : '100%')};
  overflow: hidden;
  position: relative;
  z-index: 0;
  margin-bottom: 20px;

  ${({ foldedWithPicture }) =>
    foldedWithPicture &&
    css`
      div + div {
        p + * {
          display: none;
        }
      }
    `}
  ${({ foldedWithText }) =>
    foldedWithText &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 74%);
      }
    `}
  ${plume.styles.heading.L} {
    margin-bottom: 10px;
  }

  img {
    margin: 10px 0;
  }
`

const BlurredBackground = styled(styles.Blurred)`
  justify-content: center;
  align-items: center;
  align-content: center;
  flex-direction: column;
  padding: 30px 0 0;
  filter: none;

  svg {
    fill: ${plume.COLORS.GREY_SHADE_3};
  }

  ${plume.styles.copy.M} {
    margin: 15px 0;
  }

  &::before {
    top: 20px;
    left: 0;
    filter: blur(8px);
    box-shadow:
      40px 0 0 ${plume.COLORS.GREY_SHADE_5},
      80px 0 0 ${plume.COLORS.GREY_SHADE_5},
      120px 0 0 ${plume.COLORS.GREY_SHADE_5},
      160px 0 0 ${plume.COLORS.GREY_SHADE_5},
      0px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      40px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      80px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      120px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      160px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      200px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      240px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      260px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      300px 40px 0 ${plume.COLORS.GREY_SHADE_5},
      0px 80px 0 ${plume.COLORS.GREY_SHADE_5},
      40px 80px 0 ${plume.COLORS.GREY_SHADE_5},
      80px 80px 0 ${plume.COLORS.GREY_SHADE_5},
      120px 80px 0 ${plume.COLORS.GREY_SHADE_5},
      160px 80px 0 ${plume.COLORS.GREY_SHADE_5},
      200px 80px 0 ${plume.COLORS.GREY_SHADE_5},
      240px 80px 0 ${plume.COLORS.GREY_SHADE_5},
      0px 120px 0 ${plume.COLORS.GREY_SHADE_5},
      40px 120px 0 ${plume.COLORS.GREY_SHADE_5},
      80px 120px 0 ${plume.COLORS.GREY_SHADE_5},
      120px 120px 0 ${plume.COLORS.GREY_SHADE_5},
      160px 120px 0 ${plume.COLORS.GREY_SHADE_5},
      200px 120px 0 ${plume.COLORS.GREY_SHADE_5},
      240px 120px 0 ${plume.COLORS.GREY_SHADE_5},
      260px 120px 0 ${plume.COLORS.GREY_SHADE_5};
  }
`

const Expand = styled(plume.Link)`
  bottom: 0;
  left: calc(50% - 50px);
  position: absolute;
  z-index: 20;
`

//Footer
const NewsFooter = styled.div<{ masked?: boolean }>`
  margin: 20px 0;
  display: block;
  justify-content: space-between;
  align-items: center;
  filter: ${({ masked }) => masked && 'blur(8px)'};
  pointer-events: ${({ masked }) => masked && 'none'};

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: flex;
  }
`
const Creator = styled(plume.Link)`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${plume.styles.image.Avatar} {
    margin-right: 5px;
    flex: none;
  }
`
const CreatorName = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${plume.COLORS.PRIMARY_BLACK};
  display: inline-block;
  margin-right: 5px;
  flex: none;
`
// TODO: FIXME: Should correspond to a predefined link style
const LinkWithIcon = styled.a<{ masked?: boolean }>`
  text-decoration: none;
  display: flex;
  align-items: center;
  padding: 10px 0 3px;

  &:hover {
    ${plume.glyphs.stroke.Bubble}, span {
      color: ${plume.COLORS.GREY_SHADE_2};
      fill: ${plume.COLORS.GREY_SHADE_2};
    }
  }

  ${plume.glyphs.stroke.Bubble} {
    margin-right: 5px;
    fill: ${plume.COLORS.GREY_SHADE_3};
    transition: 0.15s ease;
  }

  span {
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
    color: ${plume.COLORS.GREY_SHADE_3};
    transition: 0.15s ease;

    ${(props) => {
      if (props.masked) {
        return css`
          background: ${plume.COLORS.GREY_SHADE_4};
          border-radius: 3px;
          width: 96px;
          height: 8px;
        `
      }
    }}
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 3px 0;
  }
`
